import React, { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { onAuthChange } from '@/firebase/auth';
import type { User } from 'firebase/auth';
import { loadSettings, saveSettings, loadLastRun, saveLastRun } from '@/firebase/store';

type AppCtx = {
  user: User | null;
  booting: boolean;
};

const AuthCtx = createContext<AppCtx>({ user: null, booting: true });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    const unsub = onAuthChange((u) => {
      setUser(u);
      setBooting(false);
    });
    return () => unsub && unsub();
  }, []);

  return <AuthCtx.Provider value={{ user, booting }}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}

/* ─── Payroll data context ─── */

type PayrollState = {
  attendanceFile: File | null;
  salaryFile: File | null;
  payrollData: any[] | null;
  period: { start: string | null; end: string | null };
  rawById: Record<string, any[]>;
  adminAdj: Record<string, number>;
  skipRows: number;
  thresholds: { free_until: number; tier1: number; tier2: number; tier3: number };
  earlyThresholds: { free_until: number; tier1: number; tier2: number; tier3: number };
  missDeductions: { miss_in: number; miss_out: number };
  overtimeMode: 'rounded' | 'hourly';
  excludeLastDayMissPenalty: boolean;
  excludeEmployeesWithoutAttendance: boolean;
  excludeEmployeesWithZeroSalary: boolean;
  companyName: string;
  printEmployees: 'all' | string;
  envelopeStyle: 'detailed' | 'summary';
};

type PayrollApi = PayrollState & {
  setAttendanceFile: (f: File | null) => void;
  setSalaryFile: (f: File | null) => void;
  setPayrollData: (d: any[] | null) => void;
  setPeriod: (p: { start: string | null; end: string | null }) => void;
  setRawById: (r: Record<string, any[]>) => void;
  setAdminAdj: (a: Record<string, number>) => void;
  setSkipRows: (n: number) => void;
  setThresholds: (t: any) => void;
  setEarlyThresholds: (t: any) => void;
  setMissDeductions: (m: any) => void;
  setOvertimeMode: (m: 'rounded' | 'hourly') => void;
  setExcludeLastDayMissPenalty: (v: boolean) => void;
  setExcludeEmployeesWithoutAttendance: (v: boolean) => void;
  setExcludeEmployeesWithZeroSalary: (v: boolean) => void;
  setCompanyName: (s: string) => void;
  setPrintEmployees: (v: 'all' | string) => void;
  setEnvelopeStyle: (v: 'detailed' | 'summary') => void;
};

const PayrollCtx = createContext<PayrollApi | null>(null);

const DEFAULT_THRESHOLDS = { free_until: 15, tier1: 15, tier2: 30, tier3: 60 };

export function PayrollProvider({
  user,
  children,
}: {
  user: User | null;
  children: ReactNode;
}) {
  const [attendanceFile, setAttendanceFile] = useState<File | null>(null);
  const [salaryFile, setSalaryFile] = useState<File | null>(null);
  const [payrollData, setPayrollData] = useState<any[] | null>(null);
  const [period, setPeriod] = useState<{ start: string | null; end: string | null }>({
    start: null,
    end: null,
  });
  const [rawById, setRawById] = useState<Record<string, any[]>>({});
  const [adminAdj, setAdminAdj] = useState<Record<string, number>>({});
  const [skipRows, setSkipRows] = useState(3);
  const [thresholds, setThresholds] = useState({ ...DEFAULT_THRESHOLDS });
  const [earlyThresholds, setEarlyThresholds] = useState({ ...DEFAULT_THRESHOLDS });
  const [missDeductions, setMissDeductions] = useState({ miss_in: 0.5, miss_out: 0.5 });
  const [overtimeMode, setOvertimeMode] = useState<'rounded' | 'hourly'>('rounded');
  const [excludeLastDayMissPenalty, setExcludeLastDayMissPenalty] = useState(false);
  const [excludeEmployeesWithoutAttendance, setExcludeEmployeesWithoutAttendance] = useState(true);
  const [excludeEmployeesWithZeroSalary, setExcludeEmployeesWithZeroSalary] = useState(true);
  const [companyName, setCompanyName] = useState('شركتي');
  const [printEmployees, setPrintEmployees] = useState<'all' | string>('all');
  const [envelopeStyle, setEnvelopeStyle] = useState<'detailed' | 'summary'>('detailed');

  // Load settings from Firestore when user signs in
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const remote = await loadSettings(user.uid);
      if (cancelled || !remote) return;
      if (remote.thresholds) setThresholds((p) => ({ ...p, ...remote.thresholds }));
      if (remote.earlyThresholds) setEarlyThresholds((p) => ({ ...p, ...remote.earlyThresholds }));
      if (remote.missDeductions) setMissDeductions((p) => ({ ...p, ...remote.missDeductions }));
      if (typeof remote.overtimeMode === 'string') setOvertimeMode(remote.overtimeMode);
    if (typeof remote.excludeLastDayMissPenalty === 'boolean')
      setExcludeLastDayMissPenalty(remote.excludeLastDayMissPenalty);
    if (typeof remote.excludeEmployeesWithoutAttendance === 'boolean')
      setExcludeEmployeesWithoutAttendance(remote.excludeEmployeesWithoutAttendance);
    if (typeof remote.excludeEmployeesWithZeroSalary === 'boolean')
      setExcludeEmployeesWithZeroSalary(remote.excludeEmployeesWithZeroSalary);
    if (typeof remote.companyName === 'string' && remote.companyName.trim()) setCompanyName(remote.companyName);
      // Try restore last payroll run
      const last = await loadLastRun(user.uid);
      if (last && !cancelled && last.payrollData) {
        setPayrollData(last.payrollData);
        if (last.period) setPeriod(last.period);
        if (last.rawById) setRawById(last.rawById);
        if (last.adminAdj) setAdminAdj(last.adminAdj);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  // Auto-save settings (debounced)
  useEffect(() => {
    if (!user) return;
    const t = setTimeout(() => {
      saveSettings(user.uid, {
        thresholds,
        earlyThresholds,
        missDeductions,
        overtimeMode,
        excludeLastDayMissPenalty,
        excludeEmployeesWithoutAttendance,
        excludeEmployeesWithZeroSalary,
        companyName,
      });
    }, 1500);
    return () => clearTimeout(t);
  }, [
    user,
    thresholds,
    earlyThresholds,
    missDeductions,
    overtimeMode,
    excludeLastDayMissPenalty,
    excludeEmployeesWithoutAttendance,
    excludeEmployeesWithZeroSalary,
    companyName,
  ]);

  // Auto-save last payroll run (debounced)
  useEffect(() => {
    if (!user || !payrollData) return;
    const t = setTimeout(() => {
      saveLastRun(user.uid, { period, payrollData, rawById, adminAdj });
    }, 2000);
    return () => clearTimeout(t);
  }, [user, payrollData, period, rawById, adminAdj]);

  const value: PayrollApi = {
    attendanceFile,
    salaryFile,
    payrollData,
    period,
    rawById,
    adminAdj,
    skipRows,
    thresholds,
    earlyThresholds,
    missDeductions,
    overtimeMode,
    excludeLastDayMissPenalty,
    excludeEmployeesWithoutAttendance,
    excludeEmployeesWithZeroSalary,
    companyName,
    printEmployees,
    envelopeStyle,
    setAttendanceFile,
    setSalaryFile,
    setPayrollData,
    setPeriod,
    setRawById,
    setAdminAdj,
    setSkipRows,
    setThresholds,
    setEarlyThresholds,
    setMissDeductions,
    setOvertimeMode,
    setExcludeLastDayMissPenalty,
    setExcludeEmployeesWithoutAttendance,
    setExcludeEmployeesWithZeroSalary,
    setCompanyName,
    setPrintEmployees,
    setEnvelopeStyle,
  };

  return <PayrollCtx.Provider value={value}>{children}</PayrollCtx.Provider>;
}

export function usePayroll() {
  const ctx = useContext(PayrollCtx);
  if (!ctx) throw new Error('usePayroll must be used inside PayrollProvider');
  return ctx;
}
